import { Signal, signal } from '@angular/core';

/**
 * Controller for managing keyboard inset (visual viewport changes).
 * Provides reactive signals for keyboard height changes on mobile devices.
 */
export type KeyboardInsetController = {
  insetPx: Signal<number>;
  destroy: () => void;
};

export function createKeyboardInsetController(): KeyboardInsetController {
  const inset = signal(0);

  // If Virtual Keyboard API exists, rely on CSS env and do nothing here
  const hasVirtualKeyboardApi = typeof navigator !== 'undefined' && 'virtualKeyboard' in navigator;
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;

  if (!hasVirtualKeyboardApi && vv) {
    let lastHeight = vv.height;
    let lastOffset = vv.offsetTop;
    let pollingTimer: number | null = null;
    const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

    const update = () => {
      const layoutViewportHeight = window.innerHeight;
      const visualViewportHeight = vv.height;
      const visualViewportOffset = vv.offsetTop;

      const computedInset = layoutViewportHeight - (visualViewportHeight + visualViewportOffset);
      inset.set(computedInset > 0 ? Math.round(computedInset) : 0);

      lastHeight = visualViewportHeight;
      lastOffset = visualViewportOffset;
    };

    const resizeHandler = () => update();
    const scrollHandler = () => update();
    const orientationHandler = () => update();

    vv.addEventListener('resize', resizeHandler);
    vv.addEventListener('scroll', scrollHandler);
    window.addEventListener('orientationchange', orientationHandler);

    // On iOS, add polling when inputs are focused to catch missed resize events
    let focusHandler: (() => void) | null = null;
    let blurHandler: (() => void) | null = null;

    if (isIOS && document) {
      focusHandler = () => {
        if (pollingTimer) return;

        pollingTimer = window.setInterval(() => {
          const currentHeight = vv.height;
          const currentOffset = vv.offsetTop;

          if (currentHeight !== lastHeight || currentOffset !== lastOffset) {
            update();
          }
        }, 100);
      };

      blurHandler = () => {
        if (pollingTimer) {
          clearInterval(pollingTimer);
          pollingTimer = null;
        }
      };

      document.addEventListener('focusin', focusHandler);
      document.addEventListener('focusout', blurHandler);
    }

    update();

    return {
      insetPx: inset.asReadonly(),
      destroy: () => {
        vv.removeEventListener('resize', resizeHandler);
        vv.removeEventListener('scroll', scrollHandler);
        window.removeEventListener('orientationchange', orientationHandler);
        if (focusHandler && document) {
          document.removeEventListener('focusin', focusHandler);
        }
        if (blurHandler && document) {
          document.removeEventListener('focusout', blurHandler);
        }
        if (pollingTimer) {
          clearInterval(pollingTimer);
        }
      },
    };
  }

  return {
    insetPx: inset.asReadonly(),
    destroy: () => {},
  };
}
